import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebaseconf';
import { doc, setDoc } from 'firebase/firestore';
import { Eye, EyeOff, ArrowRight, Mail, Lock, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { createUserRoleDocument } from '../utils/authUtils';
const Signup = () => {
    const navigate = useNavigate();

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault()
        setError('');
        setLoading(true);

        // Trim inputs
        const trimmedEmail = email.trim();
        const trimmedPassword = password.trim();
        const trimmedConfirmPassword = confirmPassword.trim();

        // Validation
        if (!trimmedEmail || !trimmedPassword || !trimmedConfirmPassword) {
            setError('All fields are required');
            setLoading(false);
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            setError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        if (trimmedPassword !== trimmedConfirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        if (trimmedPassword.length < 6) {
            setError('Password should be at least 6 characters');
            setLoading(false);
            return;
        }

        // Check password strength
        const hasNumber = /\d/.test(trimmedPassword);
        const hasUpperCase = /[A-Z]/.test(trimmedPassword);
        const hasSpecialChar = /[!@#$%^&*]/.test(trimmedPassword);

        if (!hasNumber || !hasUpperCase || !hasSpecialChar) {
            setError('Password must contain at least one number, one uppercase letter and one special character');
            setLoading(false);
            return;
        }

        try {
            await createUserWithEmailAndPassword(auth, trimmedEmail, trimmedPassword)
                .then(async (userCredential) => {
                    const user = userCredential.user;
                    const normalizedEmail = trimmedEmail.toLowerCase();
                    const userName = normalizedEmail.split('@')[0];
                    const testInstitutionId = 'inst_test_college_001';
                    
                    // Create user document in Firestore
                    try {
                        const userRef = doc(db, 'institutions', testInstitutionId, 'students', user.uid);
                        
                        const firestoreUserData = {
                            id: user.uid,
                            email: normalizedEmail,
                            name: user.displayName || userName,
                            role: 'student',
                            institution_id: testInstitutionId,
                            institution_name: 'Test College XYZ',
                            createdAt: new Date().toISOString(),
                            createdVia: 'signup',
                            class: 'Not Assigned',
                            roll_number: 'N/A',
                            permissions: {}
                        };

                        // CRITICAL: Create authoritative role document in top-level users collection (STEP 4 FIX)
                        try {
                            await createUserRoleDocument(
                                user.uid,
                                normalizedEmail,
                                'student',
                                testInstitutionId,
                                user.uid,
                                { name: user.displayName || userName }
                            );
                            console.log('✅ Created role document for student in users collection');
                        } catch (roleErr) {
                            console.warn('⚠️ Warning: Could not create role document:', roleErr.message);
                        }
                        
                        await setDoc(userRef, firestoreUserData);
                        console.log('✅ User record created in Firestore:', firestoreUserData);
                        
                        // Store in session
                        const sessionUserData = {
                            id: user.uid,
                            email: normalizedEmail,
                            name: user.displayName || userName,
                            role: 'student',
                            institution_id: testInstitutionId,
                            institution_name: 'Test College XYZ',
                            signupTime: new Date().toISOString(),
                        };
                        
                        sessionStorage.setItem("logged_user", JSON.stringify(sessionUserData));
                        setLoading(false);
                        navigate("/dashboard");
                        
                    } catch (firestoreErr) {
                        console.error('❌ Error creating Firestore record:', firestoreErr);
                        setError(`Account created but database record failed: ${firestoreErr.message}`);
                        setLoading(false);
                    }
                })
                .catch((error) => {
                    let errorMessage = error.message;
                    if (error.code === 'auth/email-already-in-use') {
                        errorMessage = 'This email is already registered';
                    } else if (error.code === 'auth/weak-password') {
                        errorMessage = 'Password is too weak';
                    }
                    setError(errorMessage);
                    setLoading(false);
                });
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    }

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    // Password strength calculator
    const getPasswordStrength = (pwd) => {
        let strength = 0;
        if (pwd.length >= 6) strength++;
        if (/\d/.test(pwd)) strength++;
        if (/[A-Z]/.test(pwd)) strength++;
        if (/[!@#$%^&*]/.test(pwd)) strength++;
        return strength;
    };

    const passwordStrength = getPasswordStrength(password);
    const strengthColor = passwordStrength === 0 ? '#d1d5db' : passwordStrength <= 2 ? '#f59e0b' : '#10b981';

    return (
        <div style={styles.signupContainer}>
            {/* Left Side - Design */}
            <div style={styles.designSection}>
                <div style={styles.designContent}>
                    <div style={styles.blob1}></div>
                    <div style={styles.blob2}></div>
                    <div style={styles.blob3}></div>
                    
                    <div style={styles.designText}>
                        <h2 style={styles.designTitle}>Join Us Today</h2>
                        <p style={styles.designSubtitle}>Start your learning journey and track your progress</p>
                        
                        <div style={styles.featureList}>
                            {['Create Account', 'Access Dashboard', 'Manage Grades'].map((feature, i) => (
                                <div key={i} style={styles.featureItem}>
                                    <div style={styles.featureIcon}>✓</div>
                                    <span>{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side - Form */}
            <div style={styles.formSection}>
                <div style={styles.formContent}>
                    {/* Header */}
                    <div style={styles.header}>
                        <div style={styles.logoCircle}>
                            <CheckCircle size={32} color="white" />
                        </div>
                        <h1 style={styles.title}>Create Account</h1>
                        <p style={styles.subtitle}>Join the student management system</p>
                    </div>

                    {/* Error Alert */}
                    {error && (
                        <div style={styles.alertError}>
                            <AlertCircle size={20} />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={onSubmit} style={styles.form}>
                        {/* Email Field */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Email Address
                                <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={styles.inputWrapper}>
                                <Mail size={18} style={styles.inputIcon} />
                                <input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={styles.input}
                                    required
                                />
                            </div>
                        </div>

                        {/* Password Field */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Password
                                <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={styles.inputWrapper}>
                                <Lock size={18} style={styles.inputIcon} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={styles.input}
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={handleClickShowPassword}
                                    style={styles.showPasswordBtn}
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                            {password && (
                                <div style={styles.strengthMeterContainer}>
                                    <div style={styles.strengthMeter}>
                                        <div style={{
                                            ...styles.strengthFill,
                                            width: `${(passwordStrength / 4) * 100}%`,
                                            background: strengthColor,
                                        }}></div>
                                    </div>
                                    <span style={{ ...styles.strengthText, color: strengthColor }}>
                                        {passwordStrength <= 1 ? 'Weak' : passwordStrength <= 2 ? 'Fair' : 'Strong'}
                                    </span>
                                </div>
                            )}
                            <p style={styles.helpText}>
                                Password must contain: 6+ characters, number, uppercase letter, special character (!@#$%^&*)
                            </p>
                        </div>

                        {/* Confirm Password Field */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Confirm Password
                                <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <div style={styles.inputWrapper}>
                                <Lock size={18} style={styles.inputIcon} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    style={{
                                        ...styles.input,
                                        ...(confirmPassword && password !== confirmPassword ? styles.inputError : {}),
                                    }}
                                    required
                                />
                                {confirmPassword && password === confirmPassword && (
                                    <CheckCircle size={18} style={{
                                        position: 'absolute',
                                        right: '12px',
                                        color: '#10b981',
                                        pointerEvents: 'none',
                                    }} />
                                )}
                                {confirmPassword && password !== confirmPassword && (
                                    <XCircle size={18} style={{
                                        position: 'absolute',
                                        right: '12px',
                                        color: '#ef4444',
                                        pointerEvents: 'none',
                                    }} />
                                )}
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <p style={{ ...styles.helpText, color: '#ef4444' }}>
                                    Passwords don't match
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading || (confirmPassword && password !== confirmPassword)}
                            style={{
                                ...styles.submitBtn,
                                ...(loading ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                            }}
                        >
                            {loading ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={styles.spinner}></span>
                                    Creating Account...
                                </span>
                            ) : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    Sign Up
                                    <ArrowRight size={18} />
                                </span>
                            )}
                        </button>

                        {/* Sign In Link */}
                        <p style={styles.signupLink}>
                            Already have an account?{' '}
                            <NavLink to="/login" style={styles.signupLinkHighlight}>
                                Sign in
                            </NavLink>
                        </p>
                    </form>

                    {/* Terms Info */}
                    <p style={styles.termsText}>
                        By signing up, you agree to our Terms of Service and Privacy Policy
                    </p>
                </div>
            </div>
        </div>
    );
}


const styles = {
    signupContainer: {
        display: 'flex',
        height: '100vh',
        backgroundColor: '#f9fafb',
    },

    designSection: {
        flex: 1,
        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.95), rgba(168, 85, 247, 0.95))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
    },

    designContent: {
        position: 'relative',
        zIndex: '2',
        textAlign: 'center',
        color: 'white',
        padding: '40px',
    },

    blob1: {
        position: 'absolute',
        width: '400px',
        height: '400px',
        borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
        background: 'rgba(255, 255, 255, 0.1)',
        top: '-100px',
        right: '-100px',
        animation: 'float 6s ease-in-out infinite',
    },

    blob2: {
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
        background: 'rgba(255, 255, 255, 0.05)',
        bottom: '-50px',
        left: '-50px',
        animation: 'float 8s ease-in-out infinite',
        animationDirection: 'reverse',
    },

    blob3: {
        position: 'absolute',
        width: '250px',
        height: '250px',
        borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
        background: 'rgba(255, 255, 255, 0.08)',
        top: '50%',
        left: '10%',
        animation: 'float 7s ease-in-out infinite',
    },

    designText: {
        position: 'relative',
        zIndex: '3',
    },

    designTitle: {
        fontSize: '42px',
        fontWeight: '800',
        margin: '0 0 16px 0',
        letterSpacing: '-0.5px',
    },

    designSubtitle: {
        fontSize: '18px',
        opacity: '0.95',
        margin: '0 0 40px 0',
    },

    featureList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },

    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '16px',
    },

    featureIcon: {
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
    },

    formSection: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
    },

    formContent: {
        width: '100%',
        maxWidth: '420px',
    },

    header: {
        textAlign: 'center',
        marginBottom: '40px',
    },

    logoCircle: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        marginBottom: '20px',
        boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
    },

    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#111827',
        margin: '0 0 8px 0',
    },

    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: '0',
    },

    alertError: {
        display: 'flex',
        gap: '12px',
        padding: '12px 16px',
        borderRadius: '8px',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        color: '#991b1b',
        fontSize: '14px',
        marginBottom: '20px',
        alignItems: 'center',
    },

    form: {
        marginBottom: '20px',
    },

    formGroup: {
        marginBottom: '20px',
    },

    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px',
    },

    inputWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
    },

    inputIcon: {
        position: 'absolute',
        left: '12px',
        color: '#9ca3af',
        pointerEvents: 'none',
    },

    input: {
        width: '100%',
        padding: '12px 12px 12px 40px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '14px',
        transition: 'all 200ms ease-in-out',
        boxSizing: 'border-box',
    },

    inputError: {
        borderColor: '#ef4444',
    },

    showPasswordBtn: {
        position: 'absolute',
        right: '12px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#9ca3af',
        display: 'flex',
        alignItems: 'center',
        padding: '4px',
    },

    strengthMeterContainer: {
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },

    strengthMeter: {
        flex: 1,
        height: '4px',
        background: '#e5e7eb',
        borderRadius: '2px',
        overflow: 'hidden',
    },

    strengthFill: {
        height: '100%',
        transition: 'all 300ms ease-in-out',
    },

    strengthText: {
        fontSize: '12px',
        fontWeight: '600',
        minWidth: '40px',
    },

    helpText: {
        fontSize: '12px',
        color: '#6b7280',
        marginTop: '6px',
        margin: '6px 0 0 0',
    },

    submitBtn: {
        width: '100%',
        padding: '12px 24px',
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 200ms ease-in-out',
        boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    spinner: {
        display: 'inline-block',
        width: '16px',
        height: '16px',
        border: '2px solid rgba(255,255,255,0.3)',
        borderTop: '2px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },

    signupLink: {
        textAlign: 'center',
        fontSize: '14px',
        color: '#6b7280',
        margin: '16px 0 0 0',
    },

    signupLinkHighlight: {
        color: '#10b981',
        textDecoration: 'none',
        fontWeight: '700',
    },

    termsText: {
        fontSize: '12px',
        color: '#9ca3af',
        textAlign: 'center',
        marginTop: '20px',
        lineHeight: '1.5',
    },
};

// Add keyframes animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
    @keyframes spin {
        to { transform: rotate(360deg); }
    }
    @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(20px); }
    }
    @media (max-width: 768px) {
        [style*="designSection"] {
            display: none !important;
        }
    }
`;
if (!document.head.querySelector('style[data-signup-styles]')) {
    styleSheet.setAttribute('data-signup-styles', 'true');
    document.head.appendChild(styleSheet);
}

export default Signup;
